import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import * as XLSX from "xlsx";
import saveAs from "file-saver";

export const extractDataFromExcelSheet = (
  file: File,
  sheetNumber = 0,
  minRow = 0
): Promise<Object> => {
  return new Promise<Object>((resolve, reject) => {
    try {
      let reader = new FileReader();
      let splitName = file.name.split(".");
      let fileType = splitName[splitName.length - 1].toUpperCase();
      reader.onload = (evt) => {
        try {
          let get_header_row = (sheet) => {
            let headers = [];
            let range = XLSX.utils.decode_range(sheet["!ref"]);
            let C,
              R = minRow != 0 ? minRow:range.s.r;
            for (C = range.s.c; C <= range.e.c; ++C) {
              let cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })];
              if (cell && cell.v !== undefined && cell.v !== null && String(cell.v).trim() !== "") {
                headers.push(cell.h ?? String(cell.v));
              }
            }
            return headers;
          };
          const bstr = evt.target.result;
          let wb = XLSX.read(bstr, {
            type: "binary",
            cellDates: false,
            dateNF: "mm/dd/yyyy",
          });
          if (!wb.Sheets[wb.SheetNames[sheetNumber]]) {
            reject("Invalid Data");
            return false;
          }
          if (fileType != "XLS") {
            let range = { s: { r: Infinity, c: Infinity }, e: { r: 0, c: 0 } };
            Object.keys(wb.Sheets[wb.SheetNames[sheetNumber]])
              .filter(function(x) {
                return x.charAt(0) != "!";
              })
              .map(XLSX.utils.decode_cell)
              .forEach(function(x) {
                range.s.c = Math.min(range.s.c, x.c);
                range.s.r = Math.min(range.s.r, x.r);
                range.e.c = Math.max(range.e.c, x.c);
                range.e.r = Math.max(range.e.r, x.r);
              });
            wb.Sheets[wb.SheetNames[sheetNumber]][
              "!ref"
            ] = XLSX.utils.encode_range(range);
          }
          let columns = get_header_row(wb.Sheets[wb.SheetNames[sheetNumber]]);
          let XL_row_object = XLSX.utils.sheet_to_json(
            wb.Sheets[wb.SheetNames[sheetNumber]],
            { raw: false,range: minRow  }
          );
          let output = XLSX.utils.sheet_to_json(
            wb.Sheets[wb.SheetNames[sheetNumber]],
            {
              header: 1,
              raw: true,
              range: minRow 
            }
          );
          let formattedOutput = {};
          formattedOutput[wb.SheetNames[sheetNumber]] = output;
          resolve({ formattedOutput, XL_row_object, columns });
        } catch (e) {
          reject("Invalid Data");
        }
      };
      reader.readAsBinaryString(file);
    } catch (e) {
      reject("Invalid Data");
    }
  });
};

export const getRowCount = (file: File, sheetNumber = 0) => {
  return new Promise<number>((resolve, reject) => {
    var reader = new FileReader();
    reader.onload = function(e) {
      const data = e.target.result;
      const wb = XLSX.read(data, { type: "binary" });
      if (!wb.Sheets[wb.SheetNames[sheetNumber]]) {
        resolve(null);
      }
      const range = XLSX.utils.decode_range(
        wb.Sheets[wb.SheetNames[sheetNumber]]["!ref"]
      );
      resolve(range.e.r);
    };
    reader.readAsBinaryString(file);
  });
};

export const downloadExcelForDataGrid = ( dataGridInstance : any , downloadExcelName : string  ) : Promise<void> => {
  return new Promise<void>((resolve ,reject) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(downloadExcelName);
      exportDataGrid({
        component: dataGridInstance,
        worksheet,
        keepColumnWidths: true,
      }).then(function() {
        workbook.xlsx
          .writeBuffer()
          .then(function(buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              downloadExcelName+".xlsx"
            );
        
            resolve();
          
          })
          .catch((ex) => {
            reject(ex);
          });
  }) ;

  });
}

/**
 * Generates and downloads an Excel sheet from a given data array.
 *
 * @param dataList - The array of objects containing the data to be exported.
 * @param sheetName - The desired sheet name and file name (invalid characters will be replaced).
 * @param sheetColumns - A mapping of column headers to object keys in dataList.
 *
 * Example:
 *   const BILL_DATA_SHEET_COLUMNS = {
 *     Value: "billDataValue",
 *     Description: "billDataDescription",
 *   };
 *
 *   const billDataList = [
 *     { billDataValue: "B001", billDataDescription: "Electricity Bill" },
 *     { billDataValue: "B002", billDataDescription: "Water Bill" },
 *     { billDataValue: "B003", billDataDescription: "Internet Bill" },
 *   ];
 *
 *   downloadExcelSheet(billDataList, "Bill Data Sheet", BILL_DATA_SHEET_COLUMNS);
 */
export const downloadExcelSheet = (
  dataList: any[],
  sheetName: string,
  sheetColumns: object,
) => {
  const workbook = new Workbook();

  // Remove invalid Excel characters from sheet name & limit to 31 characters
  const cleanSheetName = sheetName.replace(/[\[\]\*\/\\\?\:]/g, "-").substring(0, 31);
  const worksheet = workbook.addWorksheet(cleanSheetName);

  // Define Excel columns from provided mapping: { Column Header → Object Key }
  worksheet.columns = Object.entries(sheetColumns).map(([header, key]) => ({
    header, // Column display name in Excel
    key,    // Key to fetch data from each row object
    width: header.length + 30, // Auto-width with padding
  }));

  // Add each row of data dynamically
  if (Array.isArray(dataList) && dataList.length > 0) {
    dataList.forEach((item) => {
      const rowData: Partial<any> = {};

      // Loop over the mapped keys and assign the matching values
      Object.values(sheetColumns).forEach((colKey) => {
        rowData[colKey] = item[colKey];
      });

      // Add the row to the worksheet
      worksheet.addRow(rowData);
    });
  }

  // Style the header row: make text bold
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.commit();

  // Center-align all cells
  worksheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.alignment = { horizontal: "center" };
    });
  });

  // Export the workbook as a downloadable .xlsx file
  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${cleanSheetName}.xlsx`);
  });
};